import React, { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { enlaceChatGPTWork } from "@/lib/chatgptLink";

export const DIRECCION_OLIMPO = "https://cerebro.olimpo.tech/api/mcp";
export const DIRECTORIO_CHATGPT = process.env.REACT_APP_OLIMPO_PLUGIN_URL || "https://chatgpt.com/plugins/plugin_asdk_app_6aa2bd955b0081919aea09279e308696";
export const OLIMPO_PUBLICADO = ["1", "true", "si", "sí"].includes(
  String(process.env.REACT_APP_OLIMPO_PLUGIN_PUBLIC || "").toLowerCase(),
);
export const PRUEBA_CONEXION = "@Olimpo Consultá Olimpo y decime qué procesos tengo guardados. Si no hay ninguno, decímelo. Si no podés consultar Olimpo, avisame; no respondas de memoria.";

export function MensajeParaCopiar({ texto, etiqueta = "COPIAR MENSAJE", testid }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      toast.success("Copiado. Volvé a ChatGPT, pegalo donde escribís y envialo.");
    } catch { toast.error("No pude copiarlo. Seleccioná el texto de abajo y copialo manualmente."); }
  };
  return <div className="space-y-3" data-testid={testid}>
    <button type="button" onClick={copiar} className="btn-dorado inline-flex items-center gap-2 min-h-[44px]" data-testid={`${testid}-copiar`}>
      {copiado ? <Check size={16} /> : <Copy size={16} />} {copiado ? "COPIADO" : etiqueta}
    </button>
    <details className="text-crema text-sm">
      <summary className="cursor-pointer py-2">Ver el texto para copiar a mano</summary>
      <p className="whitespace-pre-wrap break-words select-text py-2">{texto}</p>
    </details>
  </div>;
}

const SONDEO_MS = 4000;
const LIMITE_SONDEO_MS = 180000;
const ESTADOS_CONEXION = new Set(["sin_conectar", "pendiente_comprobacion", "autorizado", "comprobado", "necesita_reconectar"]);

// A token or an OAuth return alone is not evidence that ChatGPT read Olimpo.
export function useEstadoConexion() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [pausado, setPausado] = useState(false);
  const [ciclo, setCiclo] = useState(0);
  const reintentar = useCallback(() => setCiclo((n) => n + 1), []);

  useEffect(() => {
    let activo = true;
    let comprobado = false;
    let consultando = false;
    let desde = null;
    let timer = null;
    let focusTimer = null;
    let controller = null;
    const vence = Date.now() + LIMITE_SONDEO_MS;
    setError("");
    setPausado(false);

    const limpiarTimer = () => { if (timer !== null) clearTimeout(timer); timer = null; };
    const programar = () => {
      limpiarTimer();
      if (!activo || comprobado || document.visibilityState === "hidden") return;
      const restante = vence - Date.now();
      if (restante <= 0) { setPausado(true); return; }
      timer = setTimeout(consultar, Math.min(SONDEO_MS, restante));
    };
    async function consultar() {
      if (!activo || comprobado || consultando || document.visibilityState === "hidden") return;
      limpiarTimer();
      consultando = true;
      setCargando(true);
      controller = new AbortController();
      try {
        const { data } = await api.get("/conector/estado", {
          params: desde ? { desde } : undefined,
          timeout: 10000,
          signal: controller.signal,
        });
        if (!activo) return;
        if (!data || !ESTADOS_CONEXION.has(data.estado) || typeof data.autorizado !== "boolean"
          || typeof data.lectura_confirmada !== "boolean" || !Number.isFinite(Date.parse(data.checked_at))
          || (data.estado === "comprobado" && (!data.autorizado || !data.lectura_confirmada))
          || (data.lectura_confirmada && (!data.autorizado || data.estado !== "comprobado"))) {
          throw new Error("Estado de conexión incompleto");
        }
        setDatos(data);
        setError("");
        comprobado = data.estado === "comprobado" && data.autorizado === true && data.lectura_confirmada === true;
        // The first response may confirm an existing working connection. Otherwise
        // subsequent requests require a successful read after this server timestamp.
        if (!desde) desde = data.checked_at;
        if (comprobado) {
          limpiarTimer();
          if (focusTimer !== null) clearTimeout(focusTimer);
          clearTimeout(limiteTimer);
          setPausado(false);
        }
      } catch (cause) {
        if (!activo) return;
        setError(cause?.response?.status === 401
          ? "Tu sesión de Olimpo venció. Volvé a entrar con tu cuenta y reintentá."
          : "No pude comprobar la conexión en este momento. Volvé a intentar en unos instantes.");
      } finally {
        consultando = false;
        controller = null;
        if (activo) { setCargando(false); programar(); }
      }
    }
    const alVolver = () => {
      if (!activo || comprobado) return;
      if (document.visibilityState === "hidden") { limpiarTimer(); return; }
      if (focusTimer !== null) return;
      focusTimer = setTimeout(() => { focusTimer = null; consultar(); }, 150);
    };
    const limiteTimer = setTimeout(() => {
      limpiarTimer();
      if (activo && !comprobado) setPausado(true);
    }, LIMITE_SONDEO_MS);
    window.addEventListener("focus", alVolver);
    document.addEventListener("visibilitychange", alVolver);
    consultar();
    return () => {
      activo = false;
      limpiarTimer();
      clearTimeout(limiteTimer);
      if (focusTimer !== null) clearTimeout(focusTimer);
      controller?.abort();
      window.removeEventListener("focus", alVolver);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [ciclo]);

  const confirmada = datos?.estado === "comprobado" && datos.autorizado === true && datos.lectura_confirmada === true;
  return { datos, cargando, error, pausado, confirmada, reintentar };
}

function GuiaPublica({ accionesFinales, onAyuda, onComprobado }) {
  const { datos, cargando, error, pausado, confirmada, reintentar } = useEstadoConexion();
  const avisado = useRef(false);
  const callback = useRef(onComprobado);
  const [avisoAvance, setAvisoAvance] = useState("");
  callback.current = onComprobado;
  const autorizada = datos?.autorizado === true;
  const pendiente = autorizada || datos?.estado === "pendiente_comprobacion";

  useEffect(() => {
    if (!confirmada || avisado.current || !callback.current) return;
    avisado.current = true;
    let activo = true;
    const noAvanzo = () => {
      if (activo) setAvisoAvance("La conexión está comprobada. No pude actualizar este paso; usá el botón para continuar.");
    };
    try { Promise.resolve(callback.current(datos)).catch(noAvanzo); }
    catch { noAvanzo(); }
    return () => { activo = false; };
  }, [confirmada, datos]);

  const titulo = !datos ? (error ? "Volvé a comprobar tu conexión" : "Estamos comprobando tu conexión")
    : confirmada ? "Olimpo ya está conectado con ChatGPT"
      : pendiente ? "Comprobemos que ChatGPT puede leer Olimpo"
        : "Conectá Olimpo con ChatGPT";
  const estado = !datos ? (error ? "NO PUDE COMPROBAR" : "COMPROBANDO TU CONEXIÓN…")
    : confirmada ? "CONEXIÓN COMPROBADA"
      : autorizada ? "PERMISO RECIBIDO · FALTA LA CONSULTA"
        : datos.estado === "necesita_reconectar" ? "NECESITÁS VOLVER A CONECTAR"
          : datos.estado === "pendiente_comprobacion" ? "FALTA COMPROBAR LA CONEXIÓN" : "POR CONECTAR";

  return <section className="space-y-5 text-crema text-base leading-relaxed" data-testid="guia-conexion" data-modo="publico" data-estado={datos?.estado || (error ? "error" : "cargando")}>
    <p className={confirmada ? "text-verde text-sm" : "text-dorado text-sm"} aria-live="polite" role="status" data-testid="conexion-progreso">{estado}</p>
    <h3 className="text-xl font-medium" data-testid="conexion-titulo">{titulo}</h3>
    {confirmada ? <>
      <p>ChatGPT consultó Olimpo correctamente. Tu conexión ya está preparada para usar tus procesos.</p>
      {avisoAvance && <p role="alert" className="text-gris text-sm">{avisoAvance}</p>}
      {accionesFinales}
    </> : <>
      {!datos ? (!error && <p>Esperá un momento. Estamos revisando si tu cuenta ya está conectada.</p>) : !pendiente ? <>
        <p>Usá Olimpo y ChatGPT en el <b>mismo perfil del navegador</b>. Así podés aprovechar la sesión que ya abriste acá.</p>
        <a href={DIRECTORIO_CHATGPT} target="_blank" rel="noreferrer" className="btn-dorado inline-flex min-h-[44px] items-center" data-testid="conexion-abrir-chatgpt">CONECTAR CON CHATGPT</a>
        <p>En la ficha de Olimpo, tocá <b>Instalar</b>. Se abre otra pestaña: revisá el permiso y tocá <b>PERMITIR</b>. Si te pide entrar, usá la misma cuenta de Olimpo que usás acá.</p>
        <p>Dejá esta página abierta. Vamos a comprobar la conexión automáticamente.</p>
      </> : <>
        <p>{autorizada ? "El permiso ya está dado. Falta que ChatGPT haga una primera consulta." : "Tu cuenta tiene una conexión anterior. Hagamos una consulta para comprobar que sigue funcionando."}</p>
        <a href={enlaceChatGPTWork(PRUEBA_CONEXION)} target="_blank" rel="noreferrer" className="btn-dorado inline-flex min-h-[44px] items-center" data-testid="conexion-comprobar-chatgpt">COMPROBAR CON CHATGPT</a>
        <p>Se abre Work con el mensaje listo. Tocá <b>Enviar</b>. La comprobación aparecerá sola acá.</p>
      </>}
      {error && <p role="alert" className="text-crema border-l-2 border-dorado pl-3" data-testid="conexion-error">{error}</p>}
      {pausado && !error && <p className="text-gris text-sm" data-testid="conexion-pausada">La comprobación automática se pausó después de tres minutos. Podés volver a comprobar cuando termines en ChatGPT.</p>}
      {(error || pausado) && <button type="button" className="btn-fantasma min-h-[44px]" onClick={reintentar} disabled={cargando} data-testid="conexion-reintentar">{cargando ? "COMPROBANDO…" : "VOLVER A COMPROBAR"}</button>}
      {datos && <details className="text-crema text-base" data-testid="conexion-detalles">
        <summary className="cursor-pointer py-2">Si necesitás más indicaciones</summary>
        <div className="space-y-3 py-2">
          <p>Si la ficha de Olimpo ya muestra <b>Pruébalo en el chat</b>, podés usar ese botón. Antes de enviar, elegí <b>Work</b> donde aparece Chat.</p>
          <p>También podés abrir un chat nuevo en Work, escribir <b>@Olimpo</b>, seleccionarlo de la lista y enviar esta pregunta:</p>
          <MensajeParaCopiar texto={PRUEBA_CONEXION} etiqueta="COPIAR PREGUNTA DE COMPROBACIÓN" testid="conexion-prueba" />
          <p>Si ChatGPT no puede consultar Olimpo, revisá que hayas autorizado la misma cuenta de Olimpo y elegido la herramienta. Después volvé a comprobar acá.</p>
        </div>
      </details>}
    </>}
    {onAyuda && <button type="button" className="text-dorado underline min-h-[44px] text-left" onClick={onAyuda} data-testid="conexion-ayuda">No encuentro algo: necesito ayuda</button>}
  </section>;
}

function GuiaPiloto({ accionesFinales, onAyuda }) {
  const [paso, setPaso] = useState(0);
  const [copiada, setCopiada] = useState(false);
  const titulo = useRef(null);
  const cambiar = (n) => { setPaso(n); requestAnimationFrame(() => titulo.current?.focus()); };
  const copiarDireccion = async () => {
    try { await navigator.clipboard.writeText(DIRECCION_OLIMPO); setCopiada(true); }
    catch { toast.error("No pude copiarla. Seleccioná la dirección que ves abajo y copiala manualmente."); }
  };
  const titulos = ["Abrí ChatGPT y buscá Olimpo", "Habilitá la conexión personalizada", "Agregá Olimpo", "Dale permiso a ChatGPT", "Comprobá que puede leer tus procesos"];
  const pistas = [
    "La lista de complementos de tu ChatGPT. Dejá también abierta esta página de Olimpo.",
    "Modo de desarrollador encendido. Si la opción no aparece, pedí ayuda antes de seguir.",
    "Una ventana de Olimpo para entrar con tu cuenta o autorizar la conexión.",
    "La ficha de Olimpo en ChatGPT con el estado Conectado.",
    "Una respuesta que consultó Olimpo y muestra tus procesos, o confirma que todavía no tenés ninguno. Una respuesta genérica no alcanza.",
  ];
  return <section className="space-y-5 text-crema text-base leading-relaxed" data-testid="guia-conexion">
    <p className="rounded-xl border border-dorado/30 p-3 text-sm"><b>ACCESO PILOTO:</b> esta instalación es para pruebas acompañadas mientras OpenAI habilita la ficha pública de Olimpo.</p>
    <p>Olimpo guarda cómo se hace tu trabajo. ChatGPT sigue esas instrucciones y vos revisás el resultado. Esta conexión se prepara una vez por cuenta.</p>
    <p className="md:hidden rounded-xl border border-dorado/30 p-3 text-sm" data-testid="conexion-en-computadora">Para esta conexión inicial conviene usar la computadora. La guía queda disponible si preferís seguir desde acá.</p>
    <p className="text-gris">Dejá Olimpo y ChatGPT abiertos en dos pestañas. Podés volver a esta guía mientras completás cada parte.</p>
    <p className="text-dorado text-sm" aria-live="polite" data-testid="conexion-progreso">CONEXIÓN · {paso + 1} DE 5</p>
    <h3 ref={titulo} tabIndex={-1} className="text-xl font-medium focus:outline-none" data-testid="conexion-titulo">{titulos[paso]}</h3>
    {paso === 0 && <div className="space-y-4">
      <p>Abrí ChatGPT en el navegador y entrá con la cuenta que usás para trabajar. El botón de abajo abre sus complementos: ahí se agregan herramientas como Olimpo.</p>
      <a href="https://chatgpt.com/plugins" target="_blank" rel="noreferrer" className="btn-dorado inline-flex min-h-[44px] items-center" data-testid="conexion-abrir-chatgpt">ABRIR COMPLEMENTOS DE CHATGPT</a>
      <p>Si Olimpo ya figura conectado, no lo crees otra vez.</p>
      <button type="button" className="btn-fantasma min-h-[44px]" onClick={() => cambiar(4)} data-testid="conexion-ya-conectado">YA ESTÁ CONECTADO: COMPROBAR</button>
    </div>}
    {paso === 1 && <div className="space-y-4">
      <p>En ChatGPT, abrí tu perfil → <b>Configuración → Complementos → Modo de desarrollador</b>. También puede aparecer en <b>Seguridad e inicio de sesión</b>.</p>
      <p>Activá <b>Modo de desarrollador</b>. No hace falta programar: esa opción permite agregar la conexión de Olimpo.</p>
      <p>ChatGPT muestra un aviso de riesgo para las conexiones personalizadas. Leelo y continuá si estás de acuerdo. Dejá las demás opciones tal como están.</p>
    </div>}
    {paso === 2 && <div className="space-y-4">
      <p>Volvé a <b>Complementos</b>, tocá <b>Explorar complementos</b> y después el botón <b>+</b> (Crear app). Completá “Nuevo complemento” así:</p>
      <dl className="space-y-2 rounded-xl border border-dorado/30 p-4">
        <div><dt className="text-gris">Nombre</dt><dd>Olimpo</dd></div>
        <div><dt className="text-gris">Descripción</dt><dd>Los procesos y el cerebro de mi negocio</dd></div>
        <div><dt className="text-gris">Conexión</dt><dd>URL del servidor</dd></div>
        <div><dt className="text-gris">Dirección</dt><dd className="break-all select-text">{DIRECCION_OLIMPO}</dd></div>
        <div><dt className="text-gris">Autenticación</dt><dd>OAuth</dd></div>
      </dl>
      <button type="button" className="btn-dorado min-h-[44px]" onClick={copiarDireccion} data-testid="conexion-copiar-direccion">{copiada ? "DIRECCIÓN COPIADA" : "COPIAR DIRECCIÓN"}</button>
      <p>Pegá la dirección en el campo de ChatGPT. Dejá cerrada la configuración avanzada. Leé el aviso, marcá <b>Entiendo y quiero continuar</b> si estás de acuerdo y tocá <b>Crear</b>.</p>
    </div>}
    {paso === 3 && <div className="space-y-4">
      <p>Entrá con <b>la misma cuenta de Olimpo que estás usando acá</b>. Si aparece un formulario de bienvenida, completalo para volver a la autorización.</p>
      <p>Revisá qué acceso pide ChatGPT y tocá <b>PERMITIR</b> si estás de acuerdo. Terminá la creación en ChatGPT.</p>
      <p>Si Olimpo dice “Conectado” pero todavía no muestra acciones, tocá <b>Actualizar</b> en su ficha.</p>
    </div>}
    {paso === 4 && <div className="space-y-4">
      <p>Abrí una conversación nueva en ChatGPT. Arriba, elegí <b>Work</b>: es la vista para hacer tareas y revisar el resultado.</p>
      <p>En el lugar donde escribís, poné <b>@Olimpo</b> y elegí <b>Olimpo de la lista</b>. Después pegá y enviá este mensaje:</p>
      <MensajeParaCopiar texto={PRUEBA_CONEXION} etiqueta="COPIAR PREGUNTA DE COMPROBACIÓN" testid="conexion-prueba" />
      <p>Revisá que la respuesta haya consultado <b>Olimpo</b>. Si dice que no tiene acceso, volvé a seleccionarlo con @ y repetí la pregunta.</p>
      <p className="border-l-2 border-dorado pl-3">Cuando veas tus procesos, volvé a esta pestaña de Olimpo y seguí. Si todavía no tenés procesos, la respuesta debe decirlo después de consultar Olimpo.</p>
    </div>}
    <p className="rounded-xl border border-dorado/30 p-4" data-testid="conexion-resultado-esperado"><b>Lo que tenés que ver: </b>{pistas[paso]}</p>
    <div className="flex gap-3 flex-wrap">
      {paso > 0 && <button type="button" className="btn-fantasma min-h-[44px]" onClick={() => cambiar(paso - 1)} data-testid="conexion-anterior">ANTERIOR</button>}
      {paso < 4 && <button type="button" className="btn-dorado min-h-[44px]" onClick={() => cambiar(paso + 1)} data-testid="conexion-siguiente">SIGUIENTE INDICACIÓN</button>}
    </div>
    {paso === 4 && accionesFinales}
    {onAyuda && <button type="button" className="text-dorado underline min-h-[44px] text-left" onClick={onAyuda} data-testid="conexion-ayuda">No encuentro algo: necesito ayuda</button>}
    <p className="text-gris text-sm">Recorrer esta guía no conecta la cuenta por sí solo. La comprobación es la respuesta que ves en ChatGPT.</p>
  </section>;
}

export default function GuiaConexionOlimpo({ accionesFinales, onAyuda, onComprobado, publicado = OLIMPO_PUBLICADO }) {
  if (publicado) return <GuiaPublica accionesFinales={accionesFinales} onAyuda={onAyuda} onComprobado={onComprobado} />;
  return <GuiaPiloto accionesFinales={accionesFinales} onAyuda={onAyuda} />;
}
